import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowRight, CheckCircle2, Clock, Droplet } from 'lucide-react';
import { motion } from 'framer-motion';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1758548157466-7c454382035a?w=1200&q=85';

export default function SalleDeBainPage() {
  const navigate = useNavigate();

  const included = [
    'Démolition existant',
    'Plomberie et évacuation',
    'Électricité et éclairage',
    'Carrelage sol et murs',
    'Douche ou baignoire',
    'Meuble vasque et robinetterie',
    'WC suspendu ou posé',
    'Finitions et joints'
  ];

  const piloted = [
    'Plombier, carreleur, électricien coordonnés',
    'Vérification étanchéité à chaque étape',
    'Respect du calendrier serré',
    'Contrôle pose carrelage',
    'Test final plomberie',
    'Nettoyage de fin de chantier'
  ];

  const steps = [
    { title: 'Diagnostic', desc: 'Mesures, analyse plomberie, choix matériaux', duration: '2-3 jours' },
    { title: 'Démolition', desc: 'Retrait existant, préparation supports', duration: '2-3 jours' },
    { title: 'Plomberie', desc: 'Évacuation, alimentation, test étanchéité', duration: '3-4 jours' },
    { title: 'Carrelage', desc: 'Pose sol et murs, joints', duration: '4-6 jours' },
    { title: 'Équipements', desc: 'Douche, meuble, robinetterie, WC', duration: '2-3 jours' },
    { title: 'Finitions', desc: 'Silicone, nettoyage, réception', duration: '1 jour' }
  ];

  const faqs = [
    { q: 'Combien de temps dure une rénovation de salle de bain ?', a: 'Entre 2 et 4 semaines selon la surface et la complexité. Planning précis fourni au devis.' },
    { q: 'Peut-on utiliser la salle de bain pendant les travaux ?', a: 'Non, elle doit être condamnée. Nous organisons le chantier pour minimiser la gêne.' },
    { q: 'Les meubles et équipements sont-ils fournis ?', a: 'Oui, nous pouvons tout fournir et poser. Vous choisissez, nous installons.' },
    { q: 'Que se passe-t-il en cas de fuite découverte ?', a: 'Nous vous informons, proposons une solution, et ajustons le devis de manière transparente.' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--hr-bg)' }}>
      
      {/* HERO */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge 
                className="rounded-full px-4 py-1.5 font-['Montserrat'] text-xs font-medium uppercase"
                style={{ backgroundColor: 'var(--hr-accent)', color: 'var(--hr-accent-fg)' }}
              >
                Service premium
              </Badge>
              
              <h1 
                className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold leading-tight"
                style={{ color: 'var(--hr-text)' }}
              >
                Rénovation salle de bain pilotée
              </h1>
              
              <p className="text-xl leading-relaxed" style={{ color: 'var(--hr-text-muted)' }}>
                Transformation complète avec coordination plombier, carreleur, électricien. Vous choisissez, nous pilotons.
              </p>

              <div className="flex gap-4 pt-4">
                <Button
                  size="lg"
                  className="rounded-xl px-8"
                  style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
                  onClick={() => navigate('/devis')}
                >
                  Demander un devis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>

              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5" style={{ color: 'var(--hr-accent)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>2-4 semaines</span>
                </div>
                <div className="flex items-center gap-2">
                  <Droplet className="h-5 w-5" style={{ color: 'var(--hr-accent)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>Tout inclus</span>
                </div>
              </div>
            </div>

            <div className="media-frame">
              <img src={HERO_IMAGE} alt="Salle de bain rénovée" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* LE PROBLÈME */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-center mb-12" style={{ color: 'var(--hr-text)' }}>
              Une SDB sans coordination, c'est la catastrophe
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                'Plombier et carreleur qui se croisent mal',
                'Fuites découvertes trop tard',
                'Carrelage posé avant plomberie testée',
                'Matériaux livrés en retard',
                'Chantier qui traîne pendant 2 mois',
                'Finitions bâclées'
              ].map((pain) => (
                <div key={pain} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'var(--hr-bg)' }}>
                  <div className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--hr-danger)' }} />
                  <p className="text-base" style={{ color: 'var(--hr-text)' }}>{pain}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CE QUI EST INCLUS */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mb-4" style={{ color: 'var(--hr-text)' }}>
              Ce qui est inclus
            </h2>
            <p className="text-lg" style={{ color: 'var(--hr-text-muted)' }}>Transformation complète clé en main</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {included.map((item) => (
              <Card key={item} className="rounded-2xl border-[color:var(--hr-border)]">
                <CardContent className="p-6 flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: 'var(--hr-success)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>{item}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CE QUI EST PILOTÉ */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-['Montserrat'] text-xs font-medium uppercase tracking-wide mb-4" style={{ color: 'var(--hr-accent)' }}>
              Notre différence
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Ce que nous pilotons pour vous
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {piloted.map((item, i) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Card className="rounded-2xl border-[color:var(--hr-border)] h-full">
                  <CardContent className="p-6 flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--hr-primary)' }}>
                      <span className="text-white text-sm font-bold">{i + 1}</span>
                    </div>
                    <span className="text-base" style={{ color: 'var(--hr-text)' }}>{item}</span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DÉROULÉ */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mb-4" style={{ color: 'var(--hr-text)' }}>
              Comment ça se déroule
            </h2>
          </div>
          <div className="space-y-4">
            {steps.map((step, i) => (
              <Card key={step.title} className="rounded-2xl border-[color:var(--hr-border)]">
                <CardContent className="p-6 flex items-center gap-6">
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--hr-primary)' }}>
                    <span className="text-white font-bold text-lg">{i + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-['Figtree'] text-lg font-semibold mb-1" style={{ color: 'var(--hr-text)' }}>{step.title}</h3>
                    <p className="text-sm" style={{ color: 'var(--hr-text-muted)' }}>{step.desc}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium" style={{ color: 'var(--hr-accent)' }}>{step.duration}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Questions fréquentes
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem 
                key={faq.q} 
                value={`item-${i}`}
                className="rounded-2xl border px-6" 
                style={{ borderColor: 'var(--hr-border)', backgroundColor: 'var(--hr-bg)' }}
              >
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-['Figtree'] font-semibold" style={{ color: 'var(--hr-text)' }}>{faq.q}</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm leading-relaxed pt-2" style={{ color: 'var(--hr-text-muted)' }}>{faq.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20" style={{ backgroundColor: 'var(--hr-primary)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-primary-fg)' }}>
            Prêt à rénover votre salle de bain ?
          </h2>
          <Button
            size="lg"
            className="rounded-xl px-10 py-6 text-lg font-medium"
            style={{ backgroundColor: 'var(--hr-bg)', color: 'var(--hr-text)' }}
            onClick={() => navigate('/devis')}
          >
            Demander mon devis
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </div>
      </section>
    </div>
  );
}